import React, {
    Component,
} from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';

import {
    FadeInOutContainer,
    FadeInOut,
} from 'wanderu-animations';
import A from 'wanderu-component-react-router-link';
import {
    ContentSection,
    Layout,
    SectionTitle,
    TabContainer,
    TabList,
    TabListDropdown,
    Tab,
    TabPanel,
} from 'wanderu-components';
import { localize } from 'wanderu-i18n';
import { spreadAcross } from 'wanderu-utils';

import LinkGrid from './LinkGrid/LinkGrid';
import TabEvents from './events/TabEvents';

import styles from './InterlinkBlock.scss';

const NUM_COLUMNS = 3;

class InterlinkBlock extends Component {
    static propTypes = {
        tabData: PropTypes.oneOfType([
            PropTypes.object,
            PropTypes.arrayOf(PropTypes.object),
        ]).isRequired,
        tabs: PropTypes.arrayOf(PropTypes.object),
        featuredLinks: PropTypes.arrayOf(PropTypes.shape({
            originName: PropTypes.string,
            destinationName: PropTypes.string,
            url: PropTypes.string,
        })),
        title: PropTypes.string.isRequired,
        subtitle: PropTypes.string,
        collapsible: PropTypes.bool,
        singleTab: PropTypes.bool,
        noBorder: PropTypes.bool,
    };

    static defaultProps = {
        subtitle: '',
        tabs: [],
        featuredLinks: [],
        collapsible: false,
        singleTab: false,
        noBorder: false,
    };

    constructor(props) {
        super(props);

        const firstTab = this.props.tabs[0] || {};
        this.state = {
            activeTabId: firstTab.id,
        };
    }

    static getDerivedStateFromProps(props, state) {
        const hasActiveTab = props.tabs.find((tab) => tab.id === state.activeTabId);
        if (hasActiveTab) {
            return null;
        }

        const firstTab = props.tabs[0] || {};
        return {
            ...state,
            activeTabId: firstTab.id,
        };
    }

    _onTabChange = (tabId) => {
        TabEvents.tabClicked();
        this.setState(() => ({ activeTabId: tabId }));
    }

    render() {
        const {
            t,
            tabData,
            tabs,
            title,
            subtitle,
            collapsible,
            singleTab,
            featuredLinks,
            noBorder,
        } = this.props;
        const {
            activeTabId,
        } = this.state;

        let data = {};
        if (singleTab) {
            data = spreadAcross(tabData, NUM_COLUMNS);
        }

        return (
            <Layout
                className={classNames(
                    styles.container,
                    { [styles.noBorder]: noBorder },
                )}
            >
                <SectionTitle
                    className={classNames(
                        styles.title,
                        { [styles.withSubtitle]: !!subtitle },
                    )}
                    htmlFor="link-grid"
                >
                    {title}
                </SectionTitle>
                {!!subtitle && (
                    <div className={styles.subtitle}>
                        {subtitle}
                    </div>
                )}

                {!!featuredLinks.length && (
                    <ContentSection
                        className={styles.featuredLinksContentSection}
                        containerClass={styles.featuredLinksContainer}
                        id="featured-links-section"
                        layoutClassName={styles.featuredLinksLayout}
                    >
                        <ul className={styles.linkSection}>
                            {featuredLinks.map((link) => {
                                const {
                                    originName,
                                    destinationName,
                                    url,
                                } = link;

                                return (
                                    <li key={`${originName}-${destinationName}`}>
                                        <A
                                            className={styles.link}
                                            href={url}
                                            variant="subdued"
                                        >
                                            {t('baseApp:origin-to-destination', {
                                                originName,
                                                destinationName,
                                            })}
                                        </A>
                                    </li>
                                );
                            })}
                        </ul>
                    </ContentSection>
                )}

                {singleTab && (
                    <LinkGrid
                        collapsible={collapsible}
                        data={data}
                        id="link-grid"
                    />
                )}

                {!!tabs.length && !singleTab && (
                    <TabContainer
                        activeId={activeTabId}
                        onChange={this._onTabChange}
                    >
                        <TabList className={styles.desktopTabList}>
                            {tabs.map((info) => (
                                <Tab
                                    id={info.id}
                                    key={info.id}
                                >
                                    {info.text}
                                </Tab>
                            ))}
                        </TabList>
                        <TabListDropdown
                            className={styles.mobileTabList}
                            tabOptions={tabs}
                        />
                        <FadeInOutContainer childClassName={styles.fade}>
                            {Object.entries(tabData).map(([tabId, columnData]) => {
                                const columns = spreadAcross(columnData, NUM_COLUMNS);

                                return (
                                    <FadeInOut
                                        key={tabId}
                                        show={activeTabId === tabId}
                                    >
                                        <TabPanel
                                            className={styles.tabPanel}
                                            id={tabId}
                                        >
                                            <LinkGrid
                                                collapsible={collapsible}
                                                data={columns}
                                                id="link-grid"
                                            />
                                        </TabPanel>
                                    </FadeInOut>
                                );
                            })}
                        </FadeInOutContainer>
                    </TabContainer>
                )}
            </Layout>
        );
    }
}

export {
    InterlinkBlock,
};

export default localize(InterlinkBlock, ['t']);
